/**
 * useMortgageMonitorAgentTools — wires the Mortgage Monitor page into
 * FutureBot.
 *
 * Tool names must match functions/FutureBot/toolsets.js (crm-mortgage-monitor).
 * The page passes one pageApi object per render; tools read it through a ref
 * so the stable closures always see live state. Write tools (enroll/dismiss)
 * re-check ownership client-side and the backend enforces it again.
 */

import { useCallback, useMemo, useRef } from 'react';
import { waitForIdle } from '../../../components/FutureBot/agentToolHelpers';
import {
  DEFAULT_MONITOR_FILTERS,
  MONITOR_SORT_KEYS,
  describeMonitorFilters,
  matchesMonitorFilters,
  sortOpportunities,
} from '../MortgageMonitor/monitorFilters';

const MONITOR_SUGGESTIONS = [
  'Who should I call first?',
  'Show me only VA loans with $150+ savings',
  'How have rates moved lately?',
];

const STATUS_TABS = ['open', 'contacted', 'enrolled', 'dismissed', 'below_threshold'];

const RATE_PRODUCTS = ['30yr_fixed', '15yr_fixed', 'fha_30yr', 'va_30yr', 'usda_30yr', 'nonqm_30yr'];

function trimOpportunity(o) {
  return {
    id: o.id,
    borrower: [o.borrower_first_name, o.borrower_last_name].filter(Boolean).join(' ') || o.borrower_email || 'Borrower',
    status: o.status,
    noteRate: o.note_rate !== null ? Number(o.note_rate) : null,
    marketRate: o.market_rate !== null ? Number(o.market_rate) : null,
    marketRateProduct: o.market_rate_product || null,
    rateDeltaBps: o.rate_delta_bps,
    estBalance: o.est_current_balance !== null ? Math.round(Number(o.est_current_balance)) : null,
    estMonthlySavings: o.est_monthly_savings !== null ? Math.round(Number(o.est_monthly_savings)) : null,
    estEquityPct: o.est_equity_pct !== null ? Number(o.est_equity_pct) : null,
    opportunityType: o.opportunity_type,
    loanType: o.mortgage_type || null,
    loanTermMonths: o.loan_term ? Number(o.loan_term) : null,
    score: Math.round(Number(o.score) || 0),
    fundedDate: o.funded_date,
    city: o.property_city,
    state: o.property_state,
    ownerName: o.owner_name || null,
    ownerEmail: o.owner_email,
    hasContact: !!o.contact_id,
    hasDeal: !!o.deal_id,
  };
}

export function useMortgageMonitorAgentTools(pageApi, { enabled = true } = {}) {
  const apiRef = useRef(pageApi);
  apiRef.current = pageApi;
  const loadingRef = pageApi.monitor.loadingRef;

  const summary = useCallback(() => {
    const api = apiRef.current;
    const data = api.monitor.data || {};
    const stats = data.stats || {};
    const settings = data.settings || {};
    const latest = (series) => (series && series.length ? series[series.length - 1] : null);
    const todaysRates = {};
    for (const product of RATE_PRODUCTS) {
      const point = latest(data.rateTrend?.[product]);
      if (point) todaysRates[product] = point;
    }
    return {
      counts: {
        open: stats.openCount ?? 0,
        contacted: stats.contactedCount ?? 0,
        enrolled: stats.enrolledCount ?? 0,
        dismissed: stats.dismissedCount ?? 0,
        belowThreshold: stats.belowThresholdCount ?? 0,
      },
      totalEstMonthlySavings: stats.totalMonthlySavings ?? 0,
      totalActionableBalance: stats.totalBalance ?? 0,
      avgRateDeltaBps: stats.avgRateDeltaBps ?? 0,
      estLoComp: stats.estLoComp ?? 0,
      loCompBps: stats.loCompBps ?? settings.loCompBps ?? 150,
      marketRates: {
        todaysRates,
        trend30yr: data.rateTrend?.['30yr_fixed'] || [],
      },
      thresholds: settings,
      scope: data.scope || 'self',
      isAdmin: !!data.isAdmin,
      onScreen: {
        statusTab: STATUS_TABS[api.statusTab] || 'open',
        search: api.search || '',
        filters: api.filters || DEFAULT_MONITOR_FILTERS,
        sort: { by: api.sortKey || 'score', dir: api.sortDir || 'desc' },
      },
    };
  }, []);

  const tools = useMemo(() => ({
    get_monitor_summary: () => summary(),

    list_refi_opportunities: async (args) => {
      const api = apiRef.current;
      const status = args?.status || STATUS_TABS[api.statusTab] || 'open';
      const limit = Number(args?.limit) || 10;

      // Show the user what the bot is looking at: switch the tab when the
      // requested status differs from the one on screen.
      const tabIndex = STATUS_TABS.indexOf(status);
      if (tabIndex >= 0 && tabIndex !== api.statusTab) {
        api.setStatusTab(tabIndex);
      }

      // Mirror the on-screen view: active filters + sort apply.
      const filters = api.filters || DEFAULT_MONITOR_FILTERS;
      const all = sortOpportunities(
        (api.monitor.data?.opportunities || [])
          .filter((o) => o.status === status)
          .filter((o) => matchesMonitorFilters(o, filters)),
        api.sortKey || 'score',
        api.sortDir || 'desc'
      );
      return {
        status,
        activeFilters: describeMonitorFilters(filters),
        totalMatching: all.length,
        showing: Math.min(limit, all.length),
        opportunities: all.slice(0, limit).map(trimOpportunity),
      };
    },

    set_monitor_filters: async (args) => {
      const api = apiRef.current;
      const a = args || {};
      const next = { ...DEFAULT_MONITOR_FILTERS };
      if (Array.isArray(a.loanTypes) && a.loanTypes.length) next.loanTypes = a.loanTypes;
      if (a.termYears !== undefined && a.termYears !== null) next.termYears = Number(a.termYears);
      if (a.minMonthlySavings !== undefined && a.minMonthlySavings !== null) next.minSavings = Number(a.minMonthlySavings);
      if (a.minBalance !== undefined && a.minBalance !== null) next.minBalance = Number(a.minBalance);
      if (a.maxBalance !== undefined && a.maxBalance !== null) next.maxBalance = Number(a.maxBalance);
      if (a.minEquityPct !== undefined && a.minEquityPct !== null) next.minEquityPct = Number(a.minEquityPct);
      if (a.minScore !== undefined && a.minScore !== null) next.minScore = Number(a.minScore);
      api.setFilters(next);

      if (a.sortBy && MONITOR_SORT_KEYS.includes(a.sortBy)) {
        api.setSortKey(a.sortBy);
        api.setSortDir(a.sortDir === 'asc' ? 'asc' : a.sortDir === 'desc' ? 'desc'
          : ['borrower', 'mortgage_type', 'owner', 'funded_date'].includes(a.sortBy) ? 'asc' : 'desc');
      } else if (a.sortDir === 'asc' || a.sortDir === 'desc') {
        api.setSortDir(a.sortDir);
      }

      const status = STATUS_TABS[api.statusTab] || 'open';
      const matching = (api.monitor.data?.opportunities || [])
        .filter((o) => o.status === status)
        .filter((o) => matchesMonitorFilters(o, next)).length;
      return {
        ok: true,
        appliedFilters: describeMonitorFilters(next),
        sort: { by: a.sortBy || api.sortKey || 'score', dir: a.sortDir || api.sortDir || 'desc' },
        matchingOnCurrentTab: matching,
        note: 'Filters REPLACE the previous set — the table now shows exactly this view.',
      };
    },

    clear_monitor_filters: async () => {
      const api = apiRef.current;
      api.setFilters({ ...DEFAULT_MONITOR_FILTERS });
      api.setSortKey('score');
      api.setSortDir('desc');
      if (api.search) api.setSearch('');
      const status = STATUS_TABS[api.statusTab] || 'open';
      const total = (api.monitor.data?.opportunities || []).filter((o) => o.status === status).length;
      return { ok: true, note: 'All filters cleared; sorted by score (hottest first).', totalOnCurrentTab: total };
    },

    enroll_refi_opportunity: async (args) => {
      const api = apiRef.current;
      const opp = (api.monitor.data?.opportunities || []).find((o) => o.id === args?.opportunityId);
      if (!opp) {
        return { error: `No opportunity with id "${args?.opportunityId}" on this page. Call list_refi_opportunities for valid ids.` };
      }
      if (!['open', 'contacted'].includes(opp.status)) {
        return { error: `"${trimOpportunity(opp).borrower}" is ${opp.status} — only open or contacted opportunities can be enrolled.` };
      }
      const result = await api.monitor.act(opp.id, 'enroll');
      if (!result.success) return { error: result.error || 'Enroll failed.' };
      await waitForIdle(loadingRef);
      return {
        ok: true,
        borrower: trimOpportunity(opp).borrower,
        tagApplied: result.tagName,
        contactCreated: !!result.contactCreated,
        note: 'The tag fires the owner\u2019s TAG_ADDED campaigns (Refi Opportunity Nurture); the opportunity is now enrolled.',
      };
    },

    dismiss_refi_opportunity: async (args) => {
      const api = apiRef.current;
      const opp = (api.monitor.data?.opportunities || []).find((o) => o.id === args?.opportunityId);
      if (!opp) {
        return { error: `No opportunity with id "${args?.opportunityId}" on this page. Call list_refi_opportunities for valid ids.` };
      }
      if (opp.status === 'dismissed') {
        return { error: `"${trimOpportunity(opp).borrower}" is already dismissed.` };
      }
      const result = await api.monitor.act(opp.id, 'dismiss');
      if (!result.success) return { error: result.error || 'Dismiss failed.' };
      await waitForIdle(loadingRef);
      return {
        ok: true,
        borrower: trimOpportunity(opp).borrower,
        note: 'Dismissed — the daily scan will not reopen it (a human can reopen from the Dismissed tab).',
      };
    },
  }), [summary, loadingRef]);

  const getContext = useCallback(async () => {
    const api = apiRef.current;
    const s = summary();
    const referenceData = {};
    if (s.isAdmin && Array.isArray(api.monitor.data?.owners)) {
      referenceData.users = api.monitor.data.owners.map((o) => ({
        id: o.ownerUid,
        name: o.ownerName || o.ownerEmail || o.ownerUid,
      }));
    }
    const conv30 = s.marketRates.todaysRates['30yr_fixed'];
    return {
      referenceData,
      stateSummary:
        `Mortgage Monitor: ${s.counts.open} open / ${s.counts.contacted} contacted opportunities, ` +
        `~$${s.totalEstMonthlySavings}/mo total est. savings, est. LO comp ~$${s.estLoComp}. ` +
        `Conventional 30yr today: ${conv30 ? `${conv30.rate}%` : 'unknown'}. ` +
        `Viewing the ${s.onScreen.statusTab} tab${s.onScreen.search ? ` (search "${s.onScreen.search}")` : ''}; ` +
        `filters: ${describeMonitorFilters(s.onScreen.filters)}; sorted by ${s.onScreen.sort.by} ${s.onScreen.sort.dir}; scope: ${s.scope}.`,
    };
  }, [summary]);

  const registration = useMemo(() => {
    if (!enabled) return null;
    return {
      pageId: 'crm-mortgage-monitor',
      title: 'Mortgage Monitor',
      tools,
      getContext,
      suggestions: MONITOR_SUGGESTIONS,
    };
  }, [enabled, tools, getContext]);

  return { registration };
}

export default useMortgageMonitorAgentTools;
